import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alerts, dashboard, services } from '@cwos/api-client';
import type { alerts as AlertsNS, dashboard as DashboardNS } from '@cwos/api-client';
import { t, type Severity } from '@cwos/ui-shared';
import {
  DeliveryBand,
  HeroGreeting,
  NeedRow,
  PausedStrip,
  QuestionTile,
  Refused,
  SinceList,
  SkeletonSet,
  SuccessEmpty,
  type NeedSeverity,
  type ReconciliationScope,
  type SinceEvent,
} from '@cwos/ui-admin';
import type { IconName } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import {
  deliveryBandScope,
  deriveDeliveryToday,
  localDayWindow,
  OWN_CASELOAD,
} from '../delivery.js';
import { SUPPRESSED_LABEL, suppressionReason } from '../dashboard/analytics/disclosure.js';
import { FIGURE_PLACEHOLDER } from '../figureState.js';
import { listPhase, type QueryLike } from '../listState.js';
import {
  CARE_GAP_CASELOAD,
  EXCEPTION_CASELOAD,
  OPEN_PAPERWORK_CASELOAD,
  queueScopeNote,
  queueSubsetWho,
} from '../dashboard/queueScope.js';
import { mayReadAlerts, mayReadIntegrationsHealth, usePermissions } from '../permissions.js';
import { useDataHealthCheck } from '../dataHealth.js';
import { attentionTrustCaveat } from '../attention/attentionLogic.js';
import { deriveAttentionCount } from '../shell/attention.js';
import { readLastSeen, writeLastSeen } from '../lastSeen.js';
import { LoadFailure } from '../LoadFailure.js';
import { WorkforceNow } from './WorkforceNow.js';
import { OpenIncidents } from './OpenIncidents.js';

type Alert = AlertsNS.Alert;

const UNRESOLVED = new Set(['open', 'acknowledged', 'assigned', 'escalated']);
const SEV_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };
const NEED_SEVERITY: Record<string, NeedSeverity> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};
const NEED_ICON: Record<string, IconName> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};

function displayName(me: { name?: string; email?: string } | null): string {
  if (!me) return '—';
  const name = me.name?.trim();
  if (name) return name;
  return me.email?.trim() ?? '—';
}

/** What a question tile puts on its three lines. */
interface TileFigures {
  answer: string;
  answerTone: Severity;
  subline: string;
}

/**
 * A QUESTION TILE'S ANSWER AND ITS SUBLINE, IN EVERY STATE THE READ CAN BE IN.
 *
 * This replaces `tileSubline`, which knew two states — a payload, or not — and
 * therefore said two things about five situations.
 *
 * WITHHELD WAS RENDERED AS FAILED. `q.isError ? 'Couldn’t be read just now'` is
 * the wording of a transient outage: it says the request went out and came back
 * wrong, and it invites the reader to come back and try. A 403 on any of these
 * four reads is not that. It is an answer — permanent, about this account — and
 * a reader who returns to the landing screen tomorrow will meet the same tile
 * having learned nothing. This codebase already separates the two everywhere
 * else it counts: `listPhase` decides `refused` before anything else and
 * regardless of cache, `ListState` renders it with no retry button at all, and
 * `figureState` holds the words for both. So the decision is `listPhase`'s and
 * the words are `figureState`'s — nothing new is spelled here.
 *
 * A DASH WAS RENDERED FOR ALL THREE NON-ANSWERS. `FIGURE_PLACEHOLDER` is
 * "what goes where the number goes", and it is deliberately never `'0'` and
 * never the same glyph twice: `…` for a read in flight, "Not shown to you" for
 * a refusal, "Didn’t load" for an outage. A reader scanning four tiles has to
 * be stopped by them rather than soothed, which one shared dash could not do.
 *
 * AND THE FRAME, WHICH NO TILE READ AT ALL. All four payloads carry a
 * `QueueScope` and all four tiles rendered their figures bare. The live case is
 * the roster: a SITE-scoped reader is failed closed on service levels and gets
 * `population: 'none'` beside `totalWorkers: 0`, so "0 below" over "0 staff"
 * read as a complete, reassuring answer about an organization that was never
 * counted. Under `none` the tile shows no figure and hands the reader the
 * server's own sentence instead; under `subset` it keeps the figure — a
 * narrowed count is a real count — and says whose it is, in `queueScope`'s
 * words rather than a fifth spelling of them.
 */
function tileFigures<T extends { scope: DashboardNS.QueueScope }>(
  query: QueryLike & { data: T | undefined },
  /** Which of `queueScope.ts`'s nouns this payload counts. */
  caseloadWho: string,
  /** The tile's three lines when a payload arrived over a population with people in it. */
  answered: (data: T) => { answer: string; tone: Severity; subline: string },
): TileFigures {
  const phase = listPhase(query);
  if (phase.kind === 'refused') {
    return {
      answer: FIGURE_PLACEHOLDER.refused,
      answerTone: 'plain',
      subline: t('figure.refused'),
    };
  }
  if (phase.kind === 'failed') {
    return { answer: FIGURE_PLACEHOLDER.failed, answerTone: 'plain', subline: t('figure.failed') };
  }
  const data = query.data;
  // `listPhase` answers `loading` for every remaining state in which no payload
  // arrived — in flight, and a query that has not been asked at all.
  if (data === undefined) {
    return { answer: FIGURE_PLACEHOLDER.loading, answerTone: 'plain', subline: 'Still loading' };
  }

  const note = queueScopeNote(data.scope, caseloadWho);
  // `none` is the one arm whose figures must not be shown. The zeros beside it
  // are real and they count nobody, so the tile's answer is the absent
  // placeholder and the whole subline is the server's reason — the same
  // treatment the five dashboard cards give it, one screen over.
  const counted = data.scope.population !== 'none' ? answered(data) : null;
  return {
    answer: counted ? counted.answer : FIGURE_PLACEHOLDER.absent,
    answerTone: counted ? counted.tone : 'plain',
    subline: [counted?.subline, note].filter((s): s is string => Boolean(s)).join(' · '),
  };
}

function unresolved(list: Alert[]): Alert[] {
  return list
    .filter((a) => UNRESOLVED.has(a.status))
    .sort((a, b) => (SEV_RANK[a.severity] ?? 3) - (SEV_RANK[b.severity] ?? 3));
}

export function RightNow() {
  const { me } = useAuth();
  const navigate = useNav();
  const qc = useQueryClient();
  const { has, isLoading: permissionsLoading, isError: permissionsError } = usePermissions();
  /**
   * The third ungated copy of the alert read. `GET /api/alerts` takes
   * `alerts:read` AND `directory:read` — alert titles name the worker they are
   * about — and this screen is the landing page, so a reader holding neither
   * met two panels built out of a 403: "What needs you" showing `SuccessEmpty`,
   * which is the sentence "nothing needs you", and "Since you were last here"
   * offering to try again forever.
   */
  const canReadAlerts = mayReadAlerts(has);
  const alertsRefused = !permissionsLoading && !permissionsError && !canReadAlerts;
  /**
   * THE CROSS-CHECK THIS CARD WAS MISSING ENTIRELY (PH-ATT-002).
   *
   * `<SuccessEmpty />` with no `message` renders the SAME "you can trust it —
   * we never hide anything" sentence `/attention` makes, and this is the first
   * thing an operator reads. `Attention.tsx` and `Insights.tsx` were the only
   * two consumers of this cross-check; the landing screen shared the copy key
   * without sharing the check behind it. See `dataHealth.ts`.
   */
  const dataHealthCheckResult = useDataHealthCheck(mayReadIntegrationsHealth(has));
  const now = useMemo(() => new Date(), []);
  const lastSeen = useRef(readLastSeen());

  // Record this visit as the new "last seen" when leaving the screen.
  useEffect(() => {
    return () => writeLastSeen(new Date());
  }, []);

  const serviceLevels = useQuery({
    queryKey: ['dashboard', 'service-levels'],
    queryFn: () => dashboard.serviceLevels(),
    refetchInterval: 30_000,
  });
  const reconciliation = useQuery({
    queryKey: ['dashboard', 'reconciliation'],
    queryFn: () => dashboard.reconciliation(),
    refetchInterval: 30_000,
  });
  const documentation = useQuery({
    queryKey: ['dashboard', 'documentation'],
    queryFn: () => dashboard.documentation(),
  });
  const careGaps = useQuery({
    queryKey: ['dashboard', 'care-gaps'],
    queryFn: () => dashboard.careGaps(),
  });
  const exceptions = useQuery({
    queryKey: ['dashboard', 'exceptions'],
    queryFn: () => dashboard.exceptions(),
  });
  /**
   * TODAY, ASKED FOR AS TODAY.
   *
   * These two reads took no window and returned every planned and delivered
   * visit in the tenant's history — 1.53 MB on a six-month tenant, refetched
   * every 30 seconds, for a band that renders one day
   * (`docs/intelligence/audit/PERFORMANCE-REVIEW.md` §5). `localDayWindow` is
   * the same closed local-day interval `deriveDeliveryToday` filters to below,
   * so nothing this screen shows changes; only the bytes do.
   *
   * The window is in the QUERY KEY. `now` is fixed at mount, so today it never
   * moves — but a cache keyed on 'today' with a window in the request would
   * serve yesterday's rows to a tab left open across midnight, which is the
   * one way a bounded read could become a wrong answer.
   */
  const todayWindow = useMemo(() => localDayWindow(now), [now]);
  const today = useQuery({
    queryKey: ['services', 'today', todayWindow.from, todayWindow.to],
    // Both responses are kept WHOLE. Each carries a `scoped` flag saying which
    // frame the caller's read came back in, and unwrapping either one to its
    // rows here would throw that flag away at the only point it exists.
    queryFn: async () => {
      const [roster, delivered] = await Promise.all([
        services.scheduled(todayWindow),
        services.events(todayWindow),
      ]);
      return { roster, delivered };
    },
    refetchInterval: 30_000,
  });
  const openAlerts = useQuery({
    queryKey: ['alerts', 'open'],
    queryFn: () => alerts.list({ limit: 100 }),
    refetchInterval: 60_000,
    enabled: canReadAlerts,
  });

  const ack = useMutation({
    mutationFn: (id: string) => alerts.acknowledge(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const name = displayName(me);

  // --- Delivery band --------------------------------------------------------
  /**
   * WHOSE VISITS THE BAND IS COUNTING — TAKEN FROM THE SERVER, NOT RE-DERIVED.
   *
   * Both reads are scoped and each is widened by a different key
   * (`schedule:manage` for the planned roster, `service:read_all` for delivered
   * visits), so a caller can hold one frame and not the other. The band divides
   * one figure by the other, so it needs both frames and it must not guess
   * either.
   *
   * THIS USED TO BE `has('service:read_all')`, with no planned-side frame at
   * all. Two things were wrong with it beyond the missing half:
   *
   *   1. `usePermissions` hands back `permissions[]` — the FLATTENED key list
   *      from `/auth/me`, with `scopeType`/`scopeId` already discarded. A
   *      program-scoped `service:read_all` is indistinguishable from an
   *      organization-wide one once flattened, so the check read a narrowed
   *      grant as whole-organization reach (61275c8, and `services/routes.ts`
   *      cites it where it chose `can()` over a membership test for the same
   *      reason). No client-side reconstruction of a server scope decision can
   *      be right here, because the client is not given the fields the decision
   *      is made from.
   *   2. `has` answers `false` while the permission set is still loading and
   *      after it fails, so an org_admin's band rendered as narrowed on every
   *      cold load — a guess, in the direction that looks safe and is not.
   *
   * The server states the frame in the response it is a fact about, and both
   * flags arrive with the rows they describe, so there is no window in which
   * the rows are present and the frame is unknown.
   */
  const delivery = today.data
    ? deriveDeliveryToday(
        today.data.roster.scheduledServices,
        today.data.delivered.serviceEvents,
        now,
        {
          plannedCoversWholeOrganization: !today.data.roster.scoped,
          deliveredCoversWholeOrganization: !today.data.delivered.scoped,
        },
        today.data.delivered.calendarIntegrationIds,
      )
    : null;
  /**
   * THE RECONCILIATION READ’S FOUR NON-ANSWERS, AND THE ONE THAT WAS STILL
   * ARRIVING AS A ZERO.
   *
   * `figureState.ts` names what a figure can be — `loading`, `refused`,
   * `failed`, `absent`, `value` — and three of the non-answers already reach
   * the band as the `null` its two alarm cells take, because all three leave
   * `reconciliation.data` undefined: the cold load, the 403, the outage.
   *
   * THE FOURTH IS NOT A MISSING PAYLOAD AT ALL. A summary that ARRIVED, whose
   * own frame says the reader reaches nobody: `scope.population === 'none'`.
   * `queueScope` (dashboard/service.ts) is explicit about what its counts then
   * are — "it matches nothing in SQL, so every figure beside it is a zero that
   * counts nobody" — and it sends those zeros, correctly, with the frame that
   * reads them. Passed straight through, `0` in the alarm channel under
   * "visits without a matching time record" is the sentence NOTHING IS
   * UNRECONCILED: the reassuring answer, manufactured out of a queue that was
   * never searched. Withheld is not zero, and a population of nobody is not an
   * all-clear.
   *
   * SO A `none` SUMMARY HANDS THE CELLS THE SAME `null` THE UNANSWERED ONES
   * DO — and it no longer hands them the same SENTENCE. `reconciliationScope`
   * below tells the band which of the four the dash stands for, so the three
   * that did not answer keep "not read" and this one says "counts nobody",
   * with the server's own reason underneath. That was the half of this defect
   * that had to wait for `packages/ui-admin`.
   */
  const reconCounted =
    reconciliation.data &&
    reconciliation.data.scope.population !== 'none' &&
    !reconciliation.data.unavailableReason
      ? reconciliation.data
      : null;
  /**
   * THE RECONCILIATION FRAME, IN THE BAND'S VOCABULARY AND NOT A FOURTH ONE.
   *
   * `QueueScope` is the dashboard domain's spelling of a frame (`population`,
   * `programIds`, `caseload`, `reason`); `ReconciliationScope` is the band's
   * (`kind`, `scope`, `reason`). The translation happens HERE, at the one place
   * that holds both, exactly as `deliveryBandScope` translates the services
   * reads' frames a few lines up — a component that took `QueueScope` directly
   * would be a UI package importing the dashboard domain's wire type, and a
   * third spelling invented in either file is the drift `queueScope.ts` exists
   * to prevent.
   *
   * THE SUBSET ARM BORROWS NO NOUN OF ITS OWN. `queueSubsetWho` builds the
   * phrase from whichever narrowings are in force, and the caseload half is
   * `OWN_CASELOAD` — the product's single sentence for the supervisor edge,
   * which `queueScope.ts` deliberately does not re-declare for this payload
   * ("a fifth constant repeating it is how the delivery band and the
   * reconciliation card would drift apart"). The reconciliation summary counts
   * unmatched visits and time records over that edge, so it is the same
   * population the delivery band names above it.
   *
   * `none` GETS NO POPULATION SPELLING. It is not a narrow set, it is no set,
   * and `ReconciliationScope` keeps it as its own arm for that reason; giving
   * it a `narrowed` phrase would let the band complete "Showing only …" with
   * something empty by construction. It carries the server's `reason` verbatim
   * instead, which is where the sentence a reader gets is actually written.
   *
   * NULL IS "NOT ANSWERED", NOT "NOT NARROWED". No payload means no frame, and
   * the band says nothing rather than promising the organization — the same
   * distinction the two counts beside it already make.
   */
  const reconciliationFrame: ReconciliationScope | null = (() => {
    const scope = reconciliation.data?.scope;
    if (!scope) return null;
    // No time clock: the two cells are not measured (owner ruling 2026-10-07).
    if (reconciliation.data?.unavailableReason === 'no_source') {
      return {
        kind: 'no_source',
        reason:
          'No time clock connected — visits are not reconciled against clock-ins for this organization.',
      };
    }
    switch (scope.population) {
      case 'organization':
        return { kind: 'organization' };
      case 'subset':
        return {
          kind: 'narrowed',
          scope: queueSubsetWho(scope.programIds, scope.caseload, OWN_CASELOAD),
        };
      case 'none':
        return { kind: 'none', reason: scope.reason };
    }
  })();
  const staleness = serviceLevels.data?.staleness;
  /**
   * WHICH READ'S FRESHNESS THE BAND'S NOTE IS ABOUT.
   *
   * The band's note used to be built from `serviceLevels.data.staleness`, and
   * the service-levels read FEEDS NOTHING ON THIS BAND. Its figures come from
   * two places: the planned/delivered/in-progress/missed cells from the
   * `services` reads, and the two alarm cells from the reconciliation summary.
   * So "as of {t} · {message}", rendered inside the card between the title and
   * the figures — which is where `StalenessNote` deliberately puts itself, so
   * a reader takes it as being about the figures under it — was reporting the
   * sync state of a roster query whose numbers are not on this band at all.
   *
   * `ReconciliationSummary.staleness` was meanwhile never read anywhere. It is
   * the only banner among the band's own sources, so it is the one the band
   * gets, and the reason SAYS WHICH TWO FIGURES IT DESCRIBES — the note sits
   * above the planned and delivered counts as well, and those come from a
   * different read that carries no banner and is not covered by this sentence.
   *
   * The service-levels banner keeps its place on "Are staff hours on track?",
   * which is the tile its numbers are actually on.
   */
  const reconStaleness = reconciliation.data?.staleness;

  // --- What needs you -------------------------------------------------------
  //
  // `needsKnown` is the difference between "nothing needs you" and "we did not
  // find out". `GET /api/alerts` 403s a caller holding `alerts:read` without
  // `directory:read` (see `alerts.ts`), so a refusal is a live path here — and
  // it used to arrive at the top of the landing screen as an all-clear.
  const needsKnown = openAlerts.data !== undefined;
  const needs = openAlerts.data ? unresolved(openAlerts.data.alerts) : [];
  const topNeeds = needs.slice(0, 3);
  /**
   * HOW MANY THINGS ACTUALLY NEED SOMEBODY — the server's count, not this
   * page's length.
   *
   * `alerts.list({ limit: 100 })` is ordered `created_at desc` across EVERY
   * status, so resolved alerts compete for the hundred rows and unresolved ones
   * fall below the cut. `needs.length` is therefore a fact about the page, and
   * it was deciding two things it is not entitled to decide: the greeting's
   * count, and — far worse — `<SuccessEmpty>`, which is this product's
   * strongest trust claim. The header bell has always counted from `total`,
   * which `countAlertsByStatus` computes over the same filter and no cap binds
   * (alerts/service.ts:655), so the badge could read 23 while the card beneath
   * it said nothing was being missed.
   *
   * `null` while the read has not answered: an unknown count is not zero, and
   * the branches above this one already own that case.
   */
  const unresolvedTotal = Array.isArray(openAlerts.data?.total)
    ? deriveAttentionCount(openAlerts.data.total)
    : null;
  /**
   * PH-ATT-003, this card's other reason (besides PH-ATT-001's data-health
   * check) it may not print the unqualified trust sentence: read off the
   * server's own `organizationWide` fact, never re-derived from `has`.
   * `undefined` while the read has not answered — an unanswered scope is not
   * evidence the organization is covered either.
   */
  const trustCaveat = attentionTrustCaveat(
    dataHealthCheckResult,
    openAlerts.data?.organizationWide,
  );

  // --- Greeting sentence (composed from live counts; never manufactures urgency)
  const urgentCount = needs.filter((a) => a.severity === 'critical').length;
  let greetingSentence: string | undefined;
  // Counted from the server's total rather than from the page, for the reason
  // above. `urgentCount` stays a page figure and is only ever a floor — it names
  // what is on screen and claims nothing about what is not.
  const needsCount = unresolvedTotal ?? needs.length;
  if (needsCount > 0) {
    greetingSentence =
      urgentCount > 0
        ? `${urgentCount} ${urgentCount === 1 ? 'thing needs' : 'things need'} you today.`
        : `${needsCount} ${needsCount === 1 ? 'item' : 'items'} worth a look.`;
  }

  // --- Since list -----------------------------------------------------------
  const sinceEvents: SinceEvent[] = (openAlerts.data?.alerts ?? [])
    .filter((a) => new Date(a.createdAt) > lastSeen.current)
    .slice(0, 6)
    .map((a) => ({
      id: a.id,
      text: a.title,
      tone: (NEED_SEVERITY[a.severity] ?? 'plain') as Severity,
      at: new Date(a.createdAt),
      href: '/attention',
    }));

  // --- Question tiles -------------------------------------------------------
  //
  // Each tile's ready answer is the only thing written here; every non-answer
  // and every frame is `tileFigures`' decision, so the four tiles cannot answer
  // a refusal, an outage or a population of nobody four different ways.
  const careGapsTile = tileFigures(careGaps, CARE_GAP_CASELOAD, (d) => ({
    answer: `${d.clientsWithGaps} of ${d.totalClientsWithTargets}`,
    tone: d.clientsWithGaps > 0 ? 'look' : 'plain',
    subline: 'off their plan',
  }));
  // `belowThreshold` NOW MEANS "BELOW THEIR OWN CONTRACT", and the subline has
  // to say who was measurable at all. It used to divide every worker by one
  // organization-wide 40, so a part-timer delivering her full 20 hours arrived
  // here inside "n below" — a false statement about a person, on the landing
  // screen. The count now covers only workers with contracted hours on file,
  // and the ones without are named rather than folded in: "48 staff · 3 with no
  // plan on file" is the honest frame, and it is a DATA gap, not a permission
  // one, so it sits beside the roster size and not in the scope note.
  //
  // AND `belowThreshold` CAN NOW BE WITHHELD, which is a THIRD kind of
  // non-answer this tile has to keep apart from the two above. A caller holding
  // `analytics:read` without `dashboard:read` reads this route de-identified,
  // and below the cohort floor the count arrives `null` because "1 below" over
  // a roster of one is that worker's plan attainment. It is not a zero (which
  // would say everybody is on plan) and not the absent placeholder (which says
  // nobody was counted) — so the tile says the word and hands over the server's
  // sentence, exactly as the `none` arm does one function up.
  const serviceLevelsTile = tileFigures(serviceLevels, OWN_CASELOAD, (d) => ({
    answer: d.belowThreshold == null ? SUPPRESSED_LABEL : `${d.belowThreshold} below`,
    tone: d.belowThreshold != null && d.belowThreshold > 0 ? 'look' : 'plain',
    // The roster size is itself a figure the frame qualifies, so it goes in
    // front of the note rather than instead of it: "48 staff · Counts the
    // programs you can see, not the whole organization."
    subline:
      d.belowThreshold == null
        ? `${d.totalWorkers} staff · ${suppressionReason(d.disclosure.suppression?.minCohortSize)}`
        : d.workersWithoutExpectedHours > 0
          ? `${d.totalWorkers} staff · ${d.workersWithoutExpectedHours} with no plan on file`
          : `${d.totalWorkers} staff`,
  }));
  const documentationTile = tileFigures(documentation, OPEN_PAPERWORK_CASELOAD, (d) => ({
    answer: `${d.totalOpen} open`,
    tone: d.totalOpen > 0 ? 'look' : 'plain',
    subline: 'notes to finish',
  }));
  const exceptionsTile = tileFigures(exceptions, EXCEPTION_CASELOAD, (d) => ({
    answer: `${d.totalActive}`,
    tone: d.totalActive > 0 ? 'look' : 'plain',
    subline: 'exceptions',
  }));

  return (
    <div className="rn-grid">
      <div className="rn-col">
        <HeroGreeting name={name} sentence={greetingSentence} needsKnown={needsKnown} now={now} />

        {/* VISUAL-PROTOTYPE ONLY: `/reconciled` renders invented concept
            data and is registered only when the fixture harness set
            `window.__CWOS_SHOW_CONCEPT_SURFACES__` before the app module
            evaluated (see App.tsx's `reconciledRoute`) — never in the real
            product. This link must not outlive that guard. The prototype's
            "Phibian noticed — 2 visits tomorrow have no worker" chip that sat
            beside it was invented data shown unguarded in production and is
            gone; a real unstaffed-visit signal would have to be computed. */}
        {typeof window !== 'undefined' && window.__CWOS_SHOW_CONCEPT_SURFACES__ === true ? (
          <div className="noticed">
            <button
              type="button"
              className="rn-link noticed-eod"
              onClick={() => navigate('/reconciled')}
            >
              End of day →
            </button>
          </div>
        ) : null}

        {/* HAPPENING NOW (Phase B): the live layer first — who is working,
            clock exceptions, today's coverage — because the command surface is
            action-first, not KPI-first (owner directive). */}
        <WorkforceNow />

        {/* What needs you */}
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">What needs you</h2>
            {needs.length > 0 ? (
              <button type="button" className="rn-link" onClick={() => navigate('/attention')}>
                All {needs.length} →
              </button>
            ) : null}
          </div>
          {alertsRefused ? (
            <Refused
              heading="Attention items are not shown to you"
              message="Reading alerts also needs permission to view the people in your organization, because an alert names the worker it is about. This is a withheld list, not an empty one — do not read it as nothing needing you."
            />
          ) : openAlerts.isLoading || permissionsLoading ? (
            <SkeletonSet surface="table" rows={3} />
          ) : openAlerts.isError && !openAlerts.data ? (
            <LoadFailure
              error={openAlerts.error}
              message="We couldn’t load attention items just now."
              onRetry={() => void openAlerts.refetch()}
            />
          ) : topNeeds.length === 0 ? (
            // NOTHING ON THE PAGE, WHICH IS THREE DIFFERENT FACTS. The all-clear
            // is licensed only by the server's own count reading zero; a count
            // above zero means the cap bound the read; and a payload that
            // carried no count at all leaves the question open. Two of the three
            // are not the trust claim, and the old code printed it for all
            // three.
            //
            // AND EVEN AT A GENUINE ZERO, THE UNQUALIFIED SENTENCE IS EARNED,
            // NOT DEFAULT (PH-ATT-002). `<SuccessEmpty />` with no `message`
            // renders "you can trust it — we never hide anything", and this
            // screen used to print it from `unresolvedTotal === 0` alone, with
            // no cross-check of any kind. `trustCaveat` is `undefined` only
            // when BOTH the data-health check came back 'clear' (PH-ATT-001)
            // AND the response said `organizationWide: true` (PH-ATT-003); on
            // any other combination the default copy is swapped for the
            // honest reason, exactly as `/attention` does.
            unresolvedTotal === 0 ? (
              <SuccessEmpty
                message={
                  trustCaveat ? `Nothing needs you right now — but ${trustCaveat}` : undefined
                }
              />
            ) : (
              <LoadFailure
                error={openAlerts.error}
                message={
                  unresolvedTotal === null
                    ? 'We couldn’t count what needs you on this read. Nothing here says the queue is clear.'
                    : `${unresolvedTotal} items need you and none of them came back on this read. Open the full list rather than reading this as nothing waiting.`
                }
                onRetry={() => void openAlerts.refetch()}
              />
            )
          ) : (
            <div>
              {openAlerts.isError ? (
                <PausedStrip asOf="" onRetry={() => void openAlerts.refetch()} />
              ) : null}
              {topNeeds.map((a) => (
                <NeedRow
                  key={a.id}
                  severity={NEED_SEVERITY[a.severity] ?? 'unsure'}
                  icon={NEED_ICON[a.severity] ?? 'unsure'}
                  title={a.title}
                  // Automation activity, where REAL (owner Phase B directive):
                  // an alert carrying a ruleId was raised by one of this
                  // organization's own automations, and saying so is honest
                  // attribution — nothing is invented for alerts people filed.
                  message={a.ruleId ? `${a.description} · Raised by an automation.` : a.description}
                  acknowledged={a.status === 'acknowledged'}
                  actions={[
                    {
                      key: 'ack',
                      label: 'Got it',
                      variant: 'primary',
                      busy: ack.isPending && ack.variables === a.id,
                      onAct: () => ack.mutate(a.id),
                    },
                    {
                      key: 'open',
                      label: 'Open',
                      onAct: () => navigate('/attention'),
                    },
                  ]}
                />
              ))}
              {/* "Got it" used to fail into nothing: the mutation had no error
                  branch, so a failed acknowledgement left the row un-noted and
                  the person who pressed the button uninformed. The alert is
                  still open — saying so is the difference between a retry and
                  a shrug. */}
              {ack.isError ? (
                <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-8)' }}>
                  That acknowledgement didn’t save — the item is still open.{' '}
                  {ack.error instanceof Error ? ack.error.message : ''}
                </p>
              ) : null}
            </div>
          )}
        </section>

        {/* INCIDENTS STILL OPEN — deliberately its own card, immediately below
            the alert queue and never inside it. An alert is Phibian noticing
            something and a person acknowledging it; an incident is a person
            saying what happened, and it ends with a written resolution rather
            than with "got it". `1765000000000_an-incident-is-not-an-alert.sql`
            is the ruling; `OpenIncidents.tsx` carries the argument. */}
        <OpenIncidents />

        {/* DeliveryBand — planned vs delivered visits today */}
        {today.isError && !today.data ? (
          <LoadFailure
            error={today.error}
            message="We couldn’t load today’s visits just now."
            onRetry={() => void today.refetch()}
          />
        ) : !delivery ? (
          <SkeletonSet surface="band" />
        ) : (
          <div>
            {today.isError ? (
              <PausedStrip asOf={delivery.asOf} onRetry={() => void today.refetch()} />
            ) : null}
            <DeliveryBand
              planned={delivery.planned}
              delivered={delivery.delivered}
              // `absent` — not derivable across two frames — reaching the band
              // as its `number | null`. The null arm of that prop is documented
              // as "not derivable, and not zero", which is the same statement;
              // the mapping is here rather than in `delivery.ts` so the
              // derivation keeps the word the rest of the app uses for it.
              inProgress={delivery.inProgress.kind === 'value' ? delivery.inProgress.value : null}
              dayElapsedPct={delivery.dayElapsedPct}
              missed={delivery.missed}
              // STILL `comparable`, AND DELIBERATELY NOT THE BAND'S SCOPE.
              // `deliveryBandScope` below can answer `shared` — two reads over
              // the same caseload — and `shared` licenses the RATIO only. The
              // set difference behind `missed` joins on `scheduledServiceId`
              // while the two lists narrow on two different worker columns, so
              // a visit planned inside the caseload and covered by a colleague
              // outside it is a phantom missed visit even when both frames
              // match. These two props therefore keep reading the `FigurePhase`s
              // `deriveDeliveryToday` computed under `comparable`, which is the
              // stricter predicate; re-deriving either from `scope.kind` would
              // look like a simplification and would restore the false alarm.
              missedKnown={delivery.missedCount.kind === 'value'}
              // The band's three states, from the two frames the server stated.
              // `organization` when both reads were whole; `shared` when both
              // were narrowed to the same named population, which keeps a sound
              // ratio for the reader it actually belongs to; otherwise
              // `narrowed` with BOTH frames named, so the band no longer has to
              // finish the sentence with a plan frame nobody told it. The
              // mapping and its `comparable` argument live in `delivery.ts`
              // beside the derivation they constrain.
              scope={deliveryBandScope(delivery.plannedPopulation, delivery.deliveredPopulation)}
              // The two directions, each under its own label. These were
              // crossed: `sessionsWithoutEvent` rendered as "visits without a
              // matching time record" (the opposite claim), and
              // `eventsWithoutSession` rendered as "delivered, not documented",
              // which is not a documentation figure at all. The real
              // documentation backlog is `documentation.totalOpen`, reported in
              // the paperwork card on the right.
              //
              // NULL, NOT ZERO, WHEN THE READ HAS NOT ANSWERED. These were
              // `recon?.x ?? 0`, and the reconciliation query has no loading
              // branch, no error branch and no refusal branch anywhere on this
              // screen — so on every cold load, and for as long as any outage
              // lasted, both alarm cells read `0`. "Nothing is unreconciled" is
              // the answer a reader stops looking after, and it was being
              // manufactured from a question nobody had answered yet.
              //
              // CLOSED — THESE TWO CELLS NOW CARRY A FRAME OF THEIR OWN.
              // `GET /api/dashboard/reconciliation` narrows by the reader's
              // program grants and by the supervisor edge, and the API states
              // the frame it answered in — `ReconciliationSummary.scope`,
              // `organization | subset{programIds, caseload} | none{reason}`
              // (apps/api/src/domains/dashboard/schemas.ts). Reaching the band
              // was blocked twice over and both halves have now lifted:
              //
              //   1. `packages/api-client`'s `ReconciliationSummary` declares
              //      `scope: QueueScope`, so `recon.scope` is readable on this
              //      side of the wire and typed.
              //   2. `DeliveryBandProps` takes `reconciliationScope`, so the
              //      two cells state whose queue they counted instead of
              //      inheriting the DELIVERY sentence that sits above them. A
              //      reader whose delivery reads are organization-wide but
              //      whose reconciliation read is program-narrowed used to see
              //      a subset count in the alarm channel under no caveat at
              //      all; the band now says so under the cells themselves.
              //
              // WHAT IS STILL NOT DONE HERE, and must not be. The two figures
              // below are NOT re-derived from the frame: a narrowed queue is a
              // real count and withholding it would be the opposite error. And
              // the DELIVERY scope prop above is left alone — it is a statement
              // about `planned` and `delivered`, and narrowing it on the
              // strength of the reconciliation frame would withdraw a sound
              // delivery ratio in order to caption a different pair of numbers.
              // The two frames are two props because they are two reads.
              visitsWithoutTimeRecord={reconCounted ? reconCounted.eventsWithoutSession : null}
              timeRecordsWithoutVisit={reconCounted ? reconCounted.sessionsWithoutEvent : null}
              // WHOSE QUEUE THE TWO COUNTS ABOVE ARE, translated into the
              // band's own vocabulary a few lines up. Null when the read has
              // not answered, which is the same thing the two counts say and
              // is deliberately not the organization.
              reconciliationScope={reconciliationFrame}
              asOf={delivery.asOf}
              stale={
                reconStaleness?.degraded
                  ? {
                      asOf: reconStaleness.lastSyncAt ?? delivery.asOf,
                      reason: `${reconStaleness.message} This describes the two reconciliation figures; today’s planned and delivered counts come from a different read.`,
                      unaffected: t('timeclock.unaffected'),
                    }
                  : undefined
              }
              onOpenMissed={(href) => navigate(href)}
              // Both open the Monitor view, which lists the reconciliation
              // items themselves. `/records/notes` and `/delivery` were not
              // routes in this app, so both cells previously led to the
              // not-found panel.
              onOpenVisitsWithoutTimeRecord={() => navigate('/dashboard?view=monitor')}
              onOpenTimeRecordsWithoutVisit={() => navigate('/dashboard?view=monitor')}
              onNavigate={navigate}
            />
          </div>
        )}

        {/* Since last visit */}
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">Since you were last here</h2>
          </div>
          {/* `openAlerts.data?.alerts ?? []` again: a failed alerts read
              rendered "nothing has happened since you were last here". */}
          {alertsRefused ? (
            <Refused
              heading="What changed is not shown to you"
              message="This list is built from alerts, and reading those needs permission to view the people in your organization as well."
            />
          ) : openAlerts.isError && openAlerts.data === undefined ? (
            <LoadFailure
              error={openAlerts.error}
              message="We couldn’t read what has changed since your last visit."
              onRetry={() => void openAlerts.refetch()}
            />
          ) : (
            <SinceList
              since={lastSeen.current}
              events={sinceEvents}
              // A failed REFRESH keeps the cached events on screen (the branch
              // above only fires when nothing ever arrived), and the tail line
              // under them says "Everything else was quiet." That claim is
              // about the whole period, so it needs a live read, not a
              // retained one.
              known={needsKnown && !openAlerts.isError}
              onOpen={(href) => navigate(href)}
              now={now}
            />
          )}
        </section>
      </div>

      {/* Right column — question tiles */}
      <div className="rn-tiles">
        <QuestionTile
          question="Are clients getting their visits?"
          answer={careGapsTile.answer}
          answerTone={careGapsTile.answerTone}
          subline={careGapsTile.subline}
          // `/delivery` is not a route in this app — the tile's answer counts
          // `GET /dashboard/care-gaps`, and the surface that lists the rows
          // behind that count is the analytics Care gaps view (the same target
          // `metricDrilldownHref` gives the care_gaps widget). Doc 06 §6.3 says
          // a number ships with a path to its rows; a path to the coming-soon
          // panel is not one.
          href="/dashboard?view=careGaps"
          stale={
            careGaps.data?.staleness?.degraded
              ? {
                  asOf: careGaps.data.staleness.lastSyncAt ?? '',
                  reason: careGaps.data.staleness.message,
                }
              : undefined
          }
          onOpen={(href) => navigate(href)}
          onNavigate={navigate}
        />
        <QuestionTile
          question="Are staff hours on track?"
          answer={serviceLevelsTile.answer}
          answerTone={serviceLevelsTile.answerTone}
          subline={serviceLevelsTile.subline}
          href="/people"
          stale={
            staleness?.degraded
              ? { asOf: staleness.lastSyncAt ?? '', reason: staleness.message }
              : undefined
          }
          onOpen={(href) => navigate(href)}
          onNavigate={navigate}
        />
        <QuestionTile
          question="Is the paperwork caught up?"
          answer={documentationTile.answer}
          answerTone={documentationTile.answerTone}
          subline={documentationTile.subline}
          // `/records/notes` is not a route either (the DeliveryBand cells
          // above met the same not-found panel before they were repointed).
          // The tile counts `GET /dashboard/documentation`; the documentation
          // view is the surface built on that read, and it is where the
          // docs_open widget already drills.
          href="/dashboard?view=documentation"
          onOpen={(href) => navigate(href)}
          onNavigate={navigate}
        />
        <QuestionTile
          question="Anything else open?"
          answer={exceptionsTile.answer}
          answerTone={exceptionsTile.answerTone}
          subline={exceptionsTile.subline}
          href="/attention"
          onOpen={(href) => navigate(href)}
          onNavigate={navigate}
        />
      </div>
    </div>
  );
}
